
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import ProjectPreorderAPI from '@/api/ProjectPreorderAPI';
import ProjectPreorderDraftAPI, { ProjectPreorderDraft } from '@/api/ProjectPreorderDraftAPI';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { notification } from 'ant-design-vue';
import moment from 'moment';
import NextStep from '@/models/NextStep';
import Attachment from '@/models/Attachment';
import { isDeliveryDateDisabled } from '@/helpers/DeliveryDateHelper';
import { ProductSystemRepository } from '@/repositories/ProductSystemRepository';
import ProductSystem from '@/models/ProductSystem';
import { orderAttachmentTypeOptions } from '@/enums/global/OrderAttachmentType';
import ProductCategory from '@/models/ProductCategory';
import ProductCategoriesRepository from '@/repositories/ProductCategoriesRepository';
import { fetchPreorderDraft, setPreorderDraft } from '@/helpers/ProjectPreorderDraftState';
import {
    determineIfProductCategoryMustBeSimple,
    transformProducts,
    transformProductSystems,
} from '@/helpers/Products/ProductHelper';

interface SavedFileRow {
    originalDocumentType: string | null;
    id: string;
    title: string;
    documentType: string | null;
}

function createPositionKey(): string {
    return `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

@Component({ name: 'ProjectPreorderFormPopup' })
export default class ProjectPreorderFormPopup extends Vue {
    @Prop({ required: true }) private project!: any;

    private visible = false;
    private currentStep = 1;
    private comment = '';
    private projectConnection = '';
    private attachmentRows: { file: any; attachmentType: string | null }[] = [{ file: null, attachmentType: null }];
    private projectFiles: SavedFileRow[] = [];
    private removedProjectFileIds: string[] = [];
    private removedDraftFileIds: string[] = [];
    private desiredDeliveryDate: any = null;
    private positions: any[] = [];
    private showValidationHint = false;
    private submitting = false;
    private saving = false;
    private addingPosition = false;
    private loadingSystems = false;
    private loadingDraft = false;
    private loadingOverlay = new LoadingOverlayHelper(this, {});

    @Watch('project.id', { immediate: true })
    private onProjectIdChange(projectId: string | number | undefined) {
        if (projectId) {
            fetchPreorderDraft(projectId);
        }
    }

    private get systems(): ProductSystem[] {
        return ProductSystemRepository.getAllShallow();
    }

    private get leadId(): string | null {
        const id = this.project?.lead?.id ?? this.project?.lead_id ?? null;
        return id ? String(id) : null;
    }

    private get productGroups(): { category: string; items: string[] }[] {
        const seen = new Set<string>();

        return ProductCategoriesRepository.getAll()
            .filter((category) => category.code !== 'MATERIALS')
            .map((category) => {
                const products = category.products ?? [];
                const tiles: any[] = determineIfProductCategoryMustBeSimple(category.id)
                    ? transformProductSystems(products.map((product) => product.productSystems ?? []).flat())
                    : transformProducts(products);
                const items: string[] = [];

                tiles.forEach((tile) => {
                    const label = (tile.productSystemName || tile.productName) as string;
                    if (label && !seen.has(label)) {
                        seen.add(label);
                        items.push(label);
                    }
                });

                return { category: category.name, items };
            })
            .filter((group) => group.items.length > 0);
    }

    private async fetchProductGroups() {
        const hasProducts = ProductCategoriesRepository.getAll().some(
            (category) => (category.products ?? []).length > 0,
        );
        if (hasProducts) {
            return;
        }

        try {
            await ProductCategory.getAll();
        } catch (e) {
            console.error('Could not load the product categories', e);
        }
    }

    private confirmSubmission(): Promise<boolean> {
        return new Promise((resolve) => {
            this.$confirm({
                title: this.$t('Confirm order') as string,
                content: this.$t(
                    'Please note: once you submit the order, the project will be locked in ROLTEKApp and further changes in ROLTEKApp will no longer be possible. If you need any changes after submitting the order, please write to us at narocila@roltek.si.',
                ) as string,
                okText: this.$t('Send') as string,
                cancelText: this.$t('Cancel') as string,
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    }

    private get attachmentTypeOptions() {
        return orderAttachmentTypeOptions;
    }

    private openPopup() {
        this.resetPopupState();
        this.projectConnection = this.project?.connection ?? '';
        this.projectFiles = this.readProjectFiles();
        if (this.projectFiles.length > 0) {
            this.attachmentRows = [];
        }
        this.loadWorkFreeDays();
        this.visible = true;
        this.loadSystemOptions();
        this.loadSavedState();
    }

    private readProjectFiles(): SavedFileRow[] {
        const attachments: any[] = this.project?.lead?.attachments ?? [];

        return attachments
            .filter((attachment) => attachment && attachment.isIncluded !== false)
            .map((attachment) => ({
                id: String(attachment.id),
                title: attachment.displayName || attachment.title,
                documentType: attachment.documentType ?? null,
                originalDocumentType: attachment.documentType ?? null,
            }));
    }

    private async loadSavedState() {
        if (!this.project?.id) {
            return;
        }

        this.loadingDraft = true;
        try {
            const data = await ProjectPreorderDraftAPI.get(String(this.project.id));
            this.projectConnection = data.connection ?? '';
            this.comment = data.comment ?? '';
            this.applyDraft(data.draft);
            if (data.draft) {
                this.currentStep = data.draft.currentStep === 2 ? 2 : 1;
            }
            setPreorderDraft(this.project.id, data.draft);
        } catch (e) {
            console.error('Could not load the saved order without configuration', e);
        } finally {
            this.loadingDraft = false;
        }
    }

        private onAttachmentRowTrash(index: number) {
        const row = this.attachmentRows[index];
        if (index === 0 && row && this.projectFiles.length === 0) {
            row.file = null;
            row.attachmentType = null;
            return;
        }
        this.removeAttachmentRow(index);
    }

    private removeAttachmentRow(index: number) {
        this.attachmentRows.splice(index, 1);
        this.ensureAttachmentRow();
    }

    private ensureAttachmentRow() {
        if (this.projectFiles.length === 0 && this.attachmentRows.length === 0) {
            this.attachmentRows.push({ file: null, attachmentType: null });
        }
    }

    private removeProjectFile(index: number) {
        const [removed] = this.projectFiles.splice(index, 1);
        if (removed) {
            this.removedProjectFileIds.push(removed.id);
        }
        this.ensureAttachmentRow();
    }

    private removePositionAttachmentRow(position: any, index: number) {
        position.attachmentRows.splice(index, 1);
        this.ensurePositionAttachmentRow(position);
    }

    private ensurePositionAttachmentRow(position: any) {
        if (position.savedFiles.length === 0 && position.attachmentRows.length === 0) {
            position.attachmentRows.push({ file: null, attachmentType: null });
        }
    }

    private removeSavedPositionFile(position: any, index: number) {
        const [removed] = position.savedFiles.splice(index, 1);
        if (removed) {
            this.removedDraftFileIds.push(removed.id);
        }
        this.ensurePositionAttachmentRow(position);
    }

    private applyDraft(draft: ProjectPreorderDraft | null) {
        this.desiredDeliveryDate = draft && draft.desiredDeliveryDate ? moment(draft.desiredDeliveryDate) : null;
        this.positions = draft
            ? draft.positions.map((position) => ({
                  key: position.key,
                  system: position.system,
                  quantity: position.quantity,
                  savedFiles: position.attachments.map((file) => ({
                      id: String(file.id),
                      title: file.displayName || file.title,
                      documentType: file.documentType,
                      originalDocumentType: file.documentType,
                  })),
                  attachmentRows: position.attachments.length > 0 ? [] : [{ file: null, attachmentType: null }],
              }))
            : [];
    }

    private get workFreeDays(): any[] {
        return this.$store.getters['tableData/workFreeDays'] ?? [];
    }

    private async loadWorkFreeDays() {
        if (this.workFreeDays.length) {
            return;
        }
        try {
            await NextStep.getWorkFreeDays();
        } catch (e) {
            // The picker still enforces the 3 week minimum without holidays.
        }
    }

    private disabledDeliveryDate(current: any): boolean {
        return isDeliveryDateDisabled(current, this.workFreeDays);
    }

    private closePopup() {
        this.visible = false;
    }

    private resetPopupState() {
        this.visible = false;
        this.currentStep = 1;
        this.comment = '';
        this.projectConnection = '';
        this.attachmentRows = [{ file: null, attachmentType: null }];
        this.projectFiles = [];
        this.removedProjectFileIds = [];
        this.removedDraftFileIds = [];
        this.desiredDeliveryDate = null;
        this.positions = [];
        this.showValidationHint = false;
        this.saving = false;
        this.loadingDraft = false;
    }

    private customRequest({ file, onSuccess }: any) {
        setTimeout(() => {
            onSuccess(file);
        }, 1000);
    }

    private handleAttachmentRowFileChange(index: number, { fileList }: any) {
        this.attachmentRows[index].file = fileList.length > 0 ? fileList[fileList.length - 1] : null;
    }

    private addAttachmentRow() {
        this.attachmentRows.push({ file: null, attachmentType: null });
    }


    private async loadSystemOptions() {
        this.loadingSystems = true;
        try {
            await Promise.all([this.fetchSystems(), this.fetchProductGroups()]);
        } finally {
            this.loadingSystems = false;
        }
    }

    private async addPosition() {
        if (this.addingPosition) {
            return;
        }

        this.addingPosition = true;
        await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

        this.positions.push({
            key: createPositionKey(),
            system: '',
            quantity: 1,
            savedFiles: [],
            attachmentRows: [{ file: null, attachmentType: null }],
        });

        await this.$nextTick();
        this.addingPosition = false;
    }

    private handlePositionAttachmentRowFileChange(position: any, index: number, { fileList }: any) {
        position.attachmentRows[index].file = fileList.length > 0 ? fileList[fileList.length - 1] : null;
    }

    private addPositionAttachmentRow(position: any) {
        position.attachmentRows.push({ file: null, attachmentType: null });
    }

    private onPositionAttachmentRowTrash(position: any, index: number) {
        const row = position.attachmentRows[index];
        if (row && row.file) {
            row.file = null;
            row.attachmentType = null;
            return;
        }
        this.removePositionAttachmentRow(position, index);
    }


    private hasAnyPositionAttachment(position: any): boolean {
        return position.attachmentRows.some((row: any) => row.file);
    }

    private removePosition(index: number) {
        this.positions.splice(index, 1);
    }

    private get validAttachmentRows() {
        return this.attachmentRows.filter((row) => row.file);
    }

    private get hasAnyAttachment() {
        return this.validAttachmentRows.length > 0;
    }

    private get hasCommentOrAttachmentSomewhere(): boolean {
        return !!(this.comment.trim() || this.validAttachmentRows.length > 0 || this.projectFiles.length > 0);
    }

    private get hasMissingAttachmentType(): boolean {
        return this.validAttachmentRows.some((row) => !row.attachmentType);
    }

    private get hasMissingPositionAttachmentType(): boolean {
        return this.positions.some((position) =>
            (position.attachmentRows ?? []).some((row: any) => row.file && !row.attachmentType),
        );
    }

    private validateAttachmentTypes(): boolean {
        if (this.hasMissingAttachmentType) {
            this.currentStep = 1;
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please select an attachment type for each attachment.') as string,
            });
            return false;
        }

        if (this.hasMissingPositionAttachmentType) {
            this.currentStep = 2;
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please select an attachment type for each position attachment.') as string,
            });
            return false;
        }

        return true;
    }

    private validateStepOne(): boolean {
        if (!this.hasCommentOrAttachmentSomewhere) {
            this.showValidationHint = true;
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please add at least a comment or an attachment.') as string,
            });
            return false;
        }

        if (this.hasMissingAttachmentType) {
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please select an attachment type for each attachment.') as string,
            });
            return false;
        }

        this.showValidationHint = false;
        return true;
    }

    private goToNextStep() {
        if (!this.validateStepOne()) {
            return;
        }
        this.currentStep = 2;
    }

   
    private async persistChanges(): Promise<{ draft: ProjectPreorderDraft; projectFileIds: string[] }> {
        const projectId = String(this.project.id);

        const saved = await ProjectPreorderDraftAPI.save(projectId, {
            connection: this.projectConnection.trim() || null,
            comment: this.comment.trim() || null,
            currentStep: this.currentStep,
            desiredDeliveryDate: this.desiredDeliveryDate
                ? moment(this.desiredDeliveryDate).format('YYYY-MM-DD HH:mm:ss')
                : null,
            positions: this.positions.map((position) => ({
                key: position.key,
                system: position.system || '',
                quantity: position.quantity || 1,
            })),
        });
        if (!saved.draft) {
            throw new Error('The draft was not saved.');
        }

        await Promise.all(this.removedProjectFileIds.map((id) => Attachment.deleteExisting(id)));
        this.removedProjectFileIds = [];

        const retypedProjectFiles = this.projectFiles.filter((file) => file.documentType !== file.originalDocumentType);
        await Promise.all(
            retypedProjectFiles.map((file) => ProjectPreorderDraftAPI.updateProjectFileType(file.id, file.documentType)),
        );
        retypedProjectFiles.forEach((file) => {
            file.originalDocumentType = file.documentType;
        });

        const newRows = this.validAttachmentRows.filter((row) => row.file?.originFileObj);
        if (newRows.length > 0) {
            if (!this.leadId) {
                throw new Error('The project has no lead to attach the files to.');
            }
            const leadId = this.leadId;
            const uploaded = await Promise.all(
                newRows.map(async (row) => {
                    const formData = new FormData();
                    formData.append('attachment', row.file.originFileObj);
                    formData.append('lead_id', leadId);
                    if (row.attachmentType) {
                        formData.append('document_type', row.attachmentType);
                    }
                    const response = await Attachment.createNew(formData);
                    return {
                        id: String(response?.data?.id),
                        title: row.file.name as string,
                        documentType: row.attachmentType,
                        originalDocumentType: row.attachmentType,
                    };
                }),
            );
            this.projectFiles.push(...uploaded);
            this.attachmentRows = [];
        }

        await Promise.all(
            this.removedDraftFileIds.map((id) => ProjectPreorderDraftAPI.removeFile(id).catch(() => undefined)),
        );
        this.removedDraftFileIds = [];

        const typeUpdates: Promise<void>[] = [];
        this.positions.forEach((position) => {
            (position.savedFiles ?? []).forEach((file: any) => {
                if (file.documentType !== file.originalDocumentType) {
                    typeUpdates.push(
                        ProjectPreorderDraftAPI.updateFileType(file.id, file.documentType).then(() => {
                            file.originalDocumentType = file.documentType;
                        }),
                    );
                }
            });
        });
        await Promise.all(typeUpdates);

        const positionUploads: Promise<void>[] = [];
        this.positions.forEach((position) => {
            const rows = (position.attachmentRows ?? []).filter((row: any) => row.file?.originFileObj);
            rows.forEach((row: any) => {
                positionUploads.push(
                    ProjectPreorderDraftAPI.uploadFile(
                        projectId,
                        row.file.originFileObj,
                        position.key,
                        row.attachmentType,
                    ).then((file) => {
                        position.savedFiles.push({
                            id: String(file.id),
                            title: file.displayName || file.title,
                            documentType: file.documentType,
                            originalDocumentType: file.documentType,
                        });
                    }),
                );
            });
        });
        await Promise.all(positionUploads);

        this.positions.forEach((position) => {
            position.attachmentRows = position.savedFiles.length > 0 ? [] : [{ file: null, attachmentType: null }];
        });

        return { draft: saved.draft, projectFileIds: this.projectFiles.map((file) => file.id) };
    }

    private async saveDraft() {
        if (this.saving || this.submitting || !this.validateAttachmentTypes()) {
            return;
        }

        try {
            this.saving = true;
            const { draft } = await this.persistChanges();
            setPreorderDraft(this.project.id, draft);
            EventBus.$emit(EventBusEvents.updateProject, { shouldUpdateOffer: false });
            notification.success({
                message: this.$t('Draft saved') as string,
                description: '',
            });
            this.closePopup();
        } catch (e) {
            console.error('Error saving the order without configuration draft', e);
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Something went wrong saving the draft') as string,
            });
        } finally {
            this.saving = false;
        }
    }

    private async submit() {
        if (!this.hasCommentOrAttachmentSomewhere) {
            this.showValidationHint = true;
            this.currentStep = 1;
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please add at least a comment or an attachment.') as string,
            });
            return;
        }

        if (!this.validateAttachmentTypes()) {
            return;
        }

        if (!(await this.confirmSubmission())) {
            return;
        }

        try {
            this.submitting = true;
            this.loadingOverlay.start();

            const { draft, projectFileIds } = await this.persistChanges();

            const validPositions = this.positions.filter((position) => position.system && position.quantity > 0);

            await ProjectPreorderAPI.createNew(String(this.project.id), this.comment.trim() || null, {
                hasPositions: validPositions.length > 0,
                desiredDeliveryDate: this.desiredDeliveryDate
                    ? moment(this.desiredDeliveryDate).format('YYYY-MM-DD HH:mm:ss')
                    : null,
                positions: validPositions.map((position) => ({
                    key: position.key,
                    system: position.system,
                    quantity: position.quantity,
                })),
                projectConnection: this.projectConnection.trim() || null,
                draftId: draft.id,
                existingAttachmentIds: projectFileIds,
            });

            setPreorderDraft(this.project.id, null);

            EventBus.$emit(EventBusEvents.updateProject, { shouldUpdateOffer: true });
            EventBus.$emit(EventBusEvents.updatePendingPreorderCount);
            notification.success({
                message: this.$t('Success') as string,
                description: this.$t('Preorder sent successfully') as string,
            });
        } catch (e) {
            console.error('Error creating preorder', e);
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Something went wrong sending the preorder') as string,
            });
        } finally {
            this.submitting = false;
            this.loadingOverlay.stop();
            this.closePopup();
        }
    }

    private async fetchSystems() {
        if (this.systems.length < 2) {
            await ProductSystem.getAll();
        }
    }

    private created() {
        EventBus.$on(EventBusEvents.showProjectPreorderFormPopup, this.openPopup);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.showProjectPreorderFormPopup, this.openPopup);
    }
}
